/**
 * Modern Specs Selectors
 *
 * Implements standardized selector architecture with:
 * - EntitySelectorFactory interface compliance
 * - Proper TypeScript typing throughout
 * - Semantic naming (no "normalized"/"denormalized")
 * - Pure domain filtering with parameters
 * - Object.freeze() immutability
 */

import { createValidatedSelector } from '../../../utils/createAppSelector';
import {
  Map,
  List
} from 'immutable';
import { DirectSelector } from '../../../types/selectors';
import { EntitySelectorFactory } from '../../../types/selectorFactory';
import { RootState } from '../../../types/redux';
import {
  createEntityListSelector,
  createEntityByIdSelector
} from '../../../store/selectors';
import { Spec } from './model';
import { keys } from './keys';


// ===== BASE ENTITY ACCESS =====

/**
 * Base selector to access specs entities from Redux state
 * Handles both Immutable and plain object state structures safely
 */
export const selectSpecsEntities = (state: RootState) => {
  const entities = state.get ? state.get('entities') : state.entities;
  if (!entities || typeof entities.get !== 'function') {
    return Map();
  }
  return entities.get(keys.state) || Map();
};

// ===== CORE SELECTORS =====

/**
 * Raw specs entities as Immutable Map
 * Use for selector composition and internal logic
 */
export const selectSpecsMap: DirectSelector<Map<string, Spec>> = createValidatedSelector(
  [selectSpecsEntities],
  (entities) => entities
);

/**
 * Specs as Immutable List for iteration and filtering
 * Use .toArray() when passing to UI components like DataTable
 */
export const selectSpecsList: DirectSelector<List<Spec>> = createEntityListSelector(
  selectSpecsEntities,
  keys.state
);

/**
 * Get single spec by ID
 * Returns null if not found
 */
export const selectSpecById = createEntityByIdSelector(selectSpecsMap);

/**
 * Total count of specs
 */
export const selectSpecsCount: DirectSelector<number> = createValidatedSelector(
  [selectSpecsList],
  (specs) => specs.size
);

// ===== STATUS SELECTORS =====

/**
 * Loading state for specs fetch operations
 */
export const selectSpecsAreFetching: DirectSelector<boolean> = createValidatedSelector(
  [(state: RootState) => state.getIn(['api', keys.state, 'fetching'], false)],
  (fetching) => !!fetching
);

/**
 * Loading state for specs mutation operations
 */
export const selectSpecsArePerforming: DirectSelector<boolean> = createValidatedSelector(
  [(state: RootState) => state.getIn(['api', keys.state, 'performing'], false)],
  (performing) => !!performing
);

// ===== DOMAIN-SPECIFIC SELECTORS =====

/**
 * Get active (non-archived) specs
 */
export const selectActiveSpecs: DirectSelector<List<Spec>> = createValidatedSelector(
  [selectSpecsList],
  (specs) => specs.filter((spec: Spec) => !spec.archivedFlag)
);

/**
 * Get archived specs
 */
export const selectArchivedSpecs: DirectSelector<List<Spec>> = createValidatedSelector(
  [selectSpecsList],
  (specs) => specs.filter((spec: Spec) => !!spec.archivedFlag)
);

/**
 * Get stats for specs (unarchived and total counts)
 */
export const selectSpecsStats: DirectSelector<{unarchived: number, total: number}> = createValidatedSelector(
  [selectActiveSpecs, selectSpecsList],
  (activeSpecs, allSpecs) => ({
    unarchived: activeSpecs.size,
    total: allSpecs.size
  })
);

// ===== SELECTOR FACTORY =====

/**
 * Standardized Specs selectors factory
 * Implements EntitySelectorFactory interface with domain-specific extensions
 */
export const SpecsSelectorsFactory: EntitySelectorFactory<Spec> & {
  // Domain-specific extensions
  active: typeof selectActiveSpecs;
  archived: typeof selectArchivedSpecs;
} = Object.freeze({
  // Core standardized interface
  map: selectSpecsMap,                    // Immutable Map<string, Spec>
  list: selectSpecsList,                  // Immutable List<Spec>
  byId: selectSpecById,                  // ParameterizedSelector<string, Spec | null>
  count: selectSpecsCount,                // DirectSelector<number>
  isFetching: selectSpecsAreFetching,     // DirectSelector<boolean>
  isPerforming: selectSpecsArePerforming, // DirectSelector<boolean>

  // Domain-specific extensions
  active: selectActiveSpecs,              // DirectSelector<List<Spec>>
  archived: selectArchivedSpecs           // DirectSelector<List<Spec>>
});
